import React from "react";
import { getSensorValues } from "../../../utils/valueEngine";

const CO2_PER_LITER_PER_DAY = 1.464;
const CO2_PER_LITER_PER_HOUR = 0.061;

const AQ_BREAKPOINTS = [
  { cLow: 0, cHigh: 30, iLow: 0, iHigh: 50, label: "Good", color: "emerald" },
  { cLow: 31, cHigh: 60, iLow: 51, iHigh: 100, label: "Satisfactory", color: "yellow" },
  { cLow: 61, cHigh: 90, iLow: 101, iHigh: 200, label: "Moderate", color: "orange" },
  { cLow: 91, cHigh: 120, iLow: 201, iHigh: 300, label: "Poor", color: "red" },
  { cLow: 121, cHigh: 250, iLow: 301, iHigh: 400, label: "Very Poor", color: "red" },
  { cLow: 251, cHigh: 500, iLow: 401, iHigh: 500, label: "Severe", color: "red" },
];

const AQ_COLORS = {
  emerald: { text: "text-emerald-600", bg: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  yellow:  { text: "text-yellow-600",  bg: "bg-yellow-400",  badge: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  orange:  { text: "text-orange-600",  bg: "bg-orange-500",  badge: "bg-orange-50 text-orange-700 border-orange-200" },
  red:     { text: "text-red-600",     bg: "bg-red-500",     badge: "bg-red-50 text-red-700 border-red-200" },
};

const norm = (value) => String(value ?? "").trim().toLowerCase();

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatNumber = (value, digits = 2) => {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const n = Number(value);
  return Number(n.toFixed(digits));
};

const includesAny = (value, words) => words.some((word) => value.includes(word));

const findSensor = (sensors, words) =>
  sensors.find((sensor) => {
    const haystack = [
      sensor?.key,
      sensor?.label,
      sensor?.unit,
      sensor?.group_type,
      sensor?.group,
      sensor?.type,
    ].map(norm).join(" ");
    return includesAny(haystack, words);
  }) || null;

const getAQStatus = (pm25) => {
  const value = Math.max(0, Number(pm25));
  const bp = AQ_BREAKPOINTS.find((item) => value <= item.cHigh) || AQ_BREAKPOINTS[AQ_BREAKPOINTS.length - 1];
  const aqi = Math.round(((bp.iHigh - bp.iLow) / (bp.cHigh - bp.cLow)) * (value - bp.cLow) + bp.iLow);

  return {
    label: bp.label,
    color: bp.color,
    aqi: Math.max(0, Math.min(500, aqi)),
    progress: Math.max(4, Math.min(100, (Math.max(0, Math.min(500, aqi)) / 500) * 100)),
  };
};

const getConfiguredRelays = (relays = []) => {
  const seen = new Set();

  return relays
    .map((relay, index) => ({
      ...relay,
      key: relay?.key || relay?.relay || relay?.channel || `i${index + 1}`,
    }))
    .filter((relay) => {
      const key = norm(relay.key);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getRelayState = (liveData, key) => {
  const raw =
    liveData?.[key] ??
    liveData?.[norm(key)] ??
    liveData?.[String(key).toUpperCase()];
  return raw === 1 || raw === true || raw === "1" || norm(raw) === "true" || norm(raw) === "on";
};

const getWorkingVolumeLiters = (device = {}, liveData = {}) => {
  const candidates = [
    device?.pbr_volume_liters,
    device?.working_volume_liters,
    device?.volume_liters,
    device?.tank_volume_liters,
    liveData?.pbr_volume_liters,
    liveData?.working_volume_liters,
    liveData?.volume_liters,
    liveData?.tank_volume_liters,
    process.env.REACT_APP_PBR_WORKING_VOLUME_LITERS,
  ];

  for (const value of candidates) {
    const n = toNumber(value);
    if (n && n > 0) return n;
  }

  return null;
};

const AQICard = ({ pm25 }) => {
  const hasValue = pm25 != null;
  const aq = hasValue ? getAQStatus(pm25) : null;
  const colors = aq ? AQ_COLORS[aq.color] : null;

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">AQI</p>
        {aq && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${colors.badge}`}>
            {aq.label}
          </span>
        )}
      </div>

      <div className="flex items-end gap-2">
        <span className={`text-4xl font-extrabold tracking-tight ${hasValue ? colors.text : "text-gray-200"}`}>
          {hasValue ? aq.aqi : "-"}
        </span>
        {hasValue && <span className="text-[12px] text-gray-400 mb-1 font-medium">PM2.5 AQI</span>}
      </div>

      <div className="space-y-1.5">
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${hasValue ? colors.bg : "bg-gray-100"}`}
            style={{ width: hasValue ? `${aq.progress}%` : "0%" }}
          />
        </div>
        <div className="flex justify-between text-[9px] text-gray-300 font-medium">
          <span>Good</span><span>Moderate</span><span>Poor</span><span>Severe</span>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, unit, accent = "text-gray-800", hint }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-5 flex flex-col gap-3">
    <div className="flex items-center justify-between gap-2">
      <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">{title}</p>
      {hint && <span className="text-[10px] font-semibold text-gray-300">{hint}</span>}
    </div>

    <div className="flex items-end gap-1.5">
      <span className={`text-4xl font-extrabold tracking-tight ${value != null ? accent : "text-gray-200"}`}>
        {value ?? "-"}
      </span>
      {value != null && (
        <span className="text-[12px] text-gray-400 mb-1 font-medium">{unit}</span>
      )}
    </div>
  </div>
);

const RelayCard = ({ number, active, label }) => (
  <div className={`
    flex flex-col items-center gap-2 p-3 rounded-xl border transition-all
    ${active ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-100"}
  `}>
    <div className={`w-3 h-3 rounded-full ${active ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.5)]" : "bg-gray-300"}`} />

    <span className={`text-[11px] font-bold ${active ? "text-emerald-700" : "text-gray-400"}`} title={label}>
      R{number}
    </span>

    <span className={`
      text-[9px] font-bold px-2 py-0.5 rounded tracking-wider
      ${active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}
    `}>
      {active ? "ON" : "OFF"}
    </span>
  </div>
);

function OverviewTab({ liveData, sensors = [], relays = [], device = {} }) {
  if (!liveData || Object.keys(liveData).length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-12 flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-[3px] border-gray-100 border-t-blue-400 animate-spin" />
        <p className="text-[13px] text-gray-400 font-medium">Waiting for live data...</p>
        <p className="text-[11px] text-gray-300">Data will appear once the device starts transmitting</p>
      </div>
    );
  }

  const pm25Sensor = findSensor(sensors, ["pm2.5", "pm25", "d5"]);
  const tempSensor = findSensor(sensors, ["temperature", "temp"]);
  const humSensor = findSensor(sensors, ["humidity"]);
  const co2Sensor = findSensor(sensors, ["co2", "carbon dioxide"]);
  const sensorValues = getSensorValues(sensors, liveData);
  const sensorValue = (sensor) => sensorValues[String(sensor?.key || "").toLowerCase()] ?? null;

  const pm25 = formatNumber(sensorValue(pm25Sensor), 1);
  const temp = formatNumber(sensorValue(tempSensor), 1);
  const humidity = formatNumber(sensorValue(humSensor), 1);
  const co2 = formatNumber(sensorValue(co2Sensor), 1);

  const workingVolume = getWorkingVolumeLiters(device, liveData);
  const co2CapturedDaily = workingVolume
    ? formatNumber(workingVolume * CO2_PER_LITER_PER_DAY, 2)
    : CO2_PER_LITER_PER_DAY;
  const co2CapturedHourly = workingVolume
    ? formatNumber(workingVolume * CO2_PER_LITER_PER_HOUR, 2)
    : CO2_PER_LITER_PER_HOUR;
  const co2Unit = workingVolume ? "g/day" : "g/L/day";
  const co2Hint = workingVolume ? `${workingVolume} L PBR` : "per liter";

  const relayList = getConfiguredRelays(relays);
  const activeRelays = relayList.filter((relay) => getRelayState(liveData, relay.key)).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        <AQICard pm25={pm25} />
        <MetricCard title="Temperature" value={temp} unit="deg C" accent="text-orange-500" />
        <MetricCard title="Humidity" value={humidity} unit="%" accent="text-blue-500" />
        <MetricCard title="CO2 Capture" value={co2CapturedDaily} unit={co2Unit} accent="text-emerald-600" hint={co2Hint} />
        <MetricCard title="Live CO2" value={co2} unit="ppm" accent="text-violet-600" />
      </div>

      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-[13px] font-semibold text-gray-700">Relay Status</h2>
            <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {relayList.length} channels
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${activeRelays > 0 ? "bg-emerald-500 animate-pulse" : "bg-gray-300"}`} />
            <span className="text-[11px] text-gray-400 font-medium">
              {activeRelays} active
            </span>
          </div>
        </div>

        {relayList.length > 0 ? (
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {relayList.map((relay, index) => (
              <RelayCard
                key={relay.key}
                number={index + 1}
                label={relay.label || relay.key}
                active={getRelayState(liveData, relay.key)}
              />
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-gray-400 font-medium">No relays configured for this device.</p>
        )}
      </div>

      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <div className="flex flex-col gap-2">
          <h2 className="text-[13px] font-semibold text-gray-700">Formula References</h2>
          <p className="text-[12px] text-gray-500">
            CO2 capture uses: daily grams = PBR volume liters x {CO2_PER_LITER_PER_DAY}; hourly grams = PBR volume liters x {CO2_PER_LITER_PER_HOUR}.
          </p>
          <p className="text-[12px] text-gray-500">
            Basis: 0.8 g dry biomass/L/day x 1.83 g CO2 per gram biomass.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] font-medium">
            <a className="text-blue-600 hover:text-blue-800" href="https://doi.org/10.3389/fbioe.2024.1387519" target="_blank" rel="noreferrer">Ashour et al. 2024</a>
            <a className="text-blue-600 hover:text-blue-800" href="https://doi.org/10.1016/j.biombioe.2014.11.008" target="_blank" rel="noreferrer">Fulke et al. 2015</a>
            <a className="text-blue-600 hover:text-blue-800" href="https://doi.org/10.1016/j.biortech.2013.03.025" target="_blank" rel="noreferrer">Li et al. 2013</a>
          </div>
          <p className="text-[11px] text-gray-400">
            Current hourly capture: {co2CapturedHourly} {workingVolume ? "g/hour" : "g/L/hour"}.
          </p>
        </div>
      </div>
    </div>
  );
}

export default OverviewTab;
